'use client';
import React, { useEffect } from 'react';
import Image from 'next/image';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const ProgressModal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  // Handle Escape key press and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[999999] p-4">
      {/* Dark overlay */}
      <div 
        className="absolute inset-0 bg-black/50 opacity-0 animate-fadeIn" 
        style={{ animationDuration: '.5s', animationFillMode: 'forwards' }}
        onClick={onClose}
      ></div>
      
      {/* Modal content with fade-in animation */}
      <div 
        className="bg-white rounded-lg md:rounded-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] overflow-y-auto relative border border-gray-200 shadow-lg box-border opacity-0 animate-fadeIn" 
        style={{ animationDuration: '.5s', animationFillMode: 'forwards' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-6 md:right-6 text-gray-500 hover:text-gray-700 z-10 w-8 h-8 md:w-auto md:h-auto flex items-center justify-center cursor-pointer"
          aria-label="Close modal"
        >
          <Image src="/x-close.svg" alt="Close" width={24} height={24} />
        </button>

        {/* Modal content */}
        <div className="p-4 md:p-11 pt-12 md:pt-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ProgressModal;
